import { Checkbox, Form, Input, Select, Upload, message } from "antd"
import { UploadOutlined } from "@ant-design/icons"
import { useEffect, useRef, useState } from "react"
import CustomModal from "../CustomModal/CustomModal"
import CustomSelect from "../CustomSelect/CustomSelect"
import { secureApi } from "../../Config/api"
import { GET, POST } from "../../utils/apis"
import { vehicleLabel } from "../../utils/helpers"

const LABELS = {
    en: {
        title: "Upload GTFS",
        replaceTitle: "Replace GTFS",
        replaceHint: "Uploading a new package replaces the current schedule of this feed. Attached vehicles stay linked and will be re-matched automatically.",
        replaceNote: "Schedule replaced — attached vehicles will be re-matched automatically from live GPS.",
        organization: "Organization",
        selectOrganization: "Select an organization",
        gtfsName: "GTFS name",
        enterGtfsName: "Enter a name for this GTFS (optional)",
        gtfsFile: "GTFS package or Excel timetables (.zip)",
        selectFile: "Select the zip file",
        fileHint: "A GTFS package, or the operator's zip of Excel timetables (one .xlsx per line) — it is converted on upload.",
        turniFiles: "Duty rosters (turni) — optional, Excel",
        selectTurni: "Add roster files (.xls / .xlsx)",
        turniHint: "Only for Excel timetables: fills each journey's shift (turno) so the Shifts view works.",
        stopsFiles: "Stop coordinates — optional",
        selectStops: "Add stop files (.csv / GTFS .zip)",
        stopsHint: "Only for Excel timetables: a stops file with coordinates (another GTFS for the same city, or an export of the stops table). Matched by stop code, then by name.",
        convertedTitle: "Converted from Excel",
        workbooksLbl: "Workbooks",
        blockIdsLbl: "Shifts matched",
        coordsResolved: "Stops with real coordinates",
        coordsUnresolved: "Stops without coordinates",
        coordsWarning: "These stops will be located automatically right after the import (looked up by name and snapped to the road). Add a stops file above for exact positions.",
        coordsBackground: (n) => `${n} stops are being located in the background from their names. They appear on the map as they are found — GTFS details → Stop coordinates shows what is still missing.`,
        tripsDroppedLbl: "Journeys dropped (no stop detail in the workbook)",
        unresolvedList: "Stops still without coordinates",
        upload: "Upload GTFS",
        attach: "Attach vehicles",
        attachVehicles: "Vehicles to associate with this GTFS",
        selectVehicles: "Select vehicles (name, plate or registering number)",
        attachOrgHint: "Only vehicles of this organization are listed; the GTFS belongs to it.",
        noVehiclesForOrg: "This organization has no vehicles yet.",
        attachedElsewhere: "already attached to another GTFS",
        attachLockedHint: "Vehicles already attached to another GTFS must be detached there first.",
        selectAll: "Select all",
        selectedCount: (n, total) => `${n} of ${total} selected`,
        uploadSuccess: "GTFS imported successfully",
        attachSuccess: "Vehicles attached to the GTFS feed",
        summary: (s) => `Lines: ${s.lines} · Stops: ${s.stops} · Trips: ${s.trips}`,
        detected: "Detected line",
        notDetected: "Line not detected",
        defaultAssigned: "Default line assigned",
        willVerify: "will be verified from live GPS",
        done: "Done",
        runsOn: "Runs",
        previewTitle: "Package preview",
        loadingPreview: "Reading the package…",
        previewFailed: "Could not read the package",
        linesLbl: "Lines",
        stopsLbl: "Stops",
        tripsLbl: "Trips",
        shiftsLbl: "Shifts",
        shapesLbl: "Shapes",
        servicePeriod: "Service period",
        reasons: {
            no_gps_today: "no GPS data today",
            bus_not_near_any_stop: "vehicle not near any scheduled stop",
            no_service_today: "no scheduled service today",
            no_scheduled_passages_nearby: "no scheduled passages nearby",
            no_trip_matches_current_time: "no run matches the current time",
            no_credible_trip_match: "GPS doesn't reliably match any run yet",
            vehicle_not_moving: "vehicle is parked — line will be detected once it moves",
            no_router_device: "no router device linked",
            detection_error: "detection failed",
        },
    },
    it: {
        title: "Carica GTFS",
        replaceTitle: "Sostituisci GTFS",
        replaceHint: "Caricando un nuovo pacchetto l'orario attuale di questo feed viene sostituito. I veicoli associati restano collegati e saranno abbinati automaticamente.",
        replaceNote: "Orario sostituito — i veicoli associati saranno abbinati automaticamente dal GPS in tempo reale.",
        organization: "Organizzazione",
        selectOrganization: "Seleziona un'organizzazione",
        gtfsName: "Nome GTFS",
        enterGtfsName: "Inserisci un nome per questo GTFS (opzionale)",
        gtfsFile: "Pacchetto GTFS o orari Excel (.zip)",
        selectFile: "Seleziona il file zip",
        fileHint: "Un pacchetto GTFS, oppure lo zip degli orari Excel dell'operatore (un .xlsx per linea): viene convertito al caricamento.",
        turniFiles: "Turni — opzionale, Excel",
        selectTurni: "Aggiungi file turni (.xls / .xlsx)",
        turniHint: "Solo per orari Excel: assegna a ogni corsa il suo turno, così la vista Turni funziona.",
        stopsFiles: "Coordinate fermate — opzionale",
        selectStops: "Aggiungi file fermate (.csv / GTFS .zip)",
        stopsHint: "Solo per orari Excel: un file fermate con coordinate (un altro GTFS della stessa città, o un export della tabella fermate). Abbinato per codice fermata, poi per nome.",
        convertedTitle: "Convertito da Excel",
        workbooksLbl: "File",
        blockIdsLbl: "Turni abbinati",
        coordsResolved: "Fermate con coordinate reali",
        coordsUnresolved: "Fermate senza coordinate",
        coordsWarning: "Queste fermate verranno posizionate automaticamente subito dopo l'importazione (cercate per nome e agganciate alla strada). Aggiungi un file fermate qui sopra per posizioni esatte.",
        coordsBackground: (n) => `${n} fermate vengono posizionate in background a partire dal nome. Compaiono sulla mappa man mano che vengono trovate: in Dettagli GTFS → Coordinate fermate vedi cosa manca ancora.`,
        tripsDroppedLbl: "Corse scartate (senza dettaglio fermate nel file)",
        unresolvedList: "Fermate ancora senza coordinate",
        upload: "Carica GTFS",
        attach: "Associa veicoli",
        attachVehicles: "Veicoli da associare a questo GTFS",
        selectVehicles: "Seleziona i veicoli (nome, targa o matricola)",
        attachOrgHint: "Vengono elencati solo i veicoli di questa organizzazione, a cui appartiene il GTFS.",
        noVehiclesForOrg: "Questa organizzazione non ha ancora veicoli.",
        attachedElsewhere: "già collegato a un altro GTFS",
        attachLockedHint: "I veicoli già collegati a un altro GTFS vanno prima scollegati da lì.",
        selectAll: "Seleziona tutti",
        selectedCount: (n, total) => `${n} di ${total} selezionati`,
        uploadSuccess: "GTFS importato con successo",
        attachSuccess: "Veicoli associati al feed GTFS",
        summary: (s) => `Linee: ${s.lines} · Fermate: ${s.stops} · Corse: ${s.trips}`,
        detected: "Linea rilevata",
        notDetected: "Linea non rilevata",
        defaultAssigned: "Linea predefinita assegnata",
        willVerify: "sarà verificata dal GPS in tempo reale",
        done: "Fine",
        runsOn: "Circola",
        previewTitle: "Anteprima del pacchetto",
        loadingPreview: "Lettura del pacchetto…",
        previewFailed: "Impossibile leggere il pacchetto",
        linesLbl: "Linee",
        stopsLbl: "Fermate",
        tripsLbl: "Corse",
        shiftsLbl: "Turni",
        shapesLbl: "Tracciati",
        servicePeriod: "Periodo di servizio",
        reasons: {
            no_gps_today: "nessun dato GPS oggi",
            bus_not_near_any_stop: "veicolo lontano dalle fermate",
            no_service_today: "nessun servizio programmato oggi",
            no_scheduled_passages_nearby: "nessun passaggio programmato vicino",
            no_trip_matches_current_time: "nessuna corsa corrisponde all'orario",
            no_credible_trip_match: "il GPS non corrisponde ancora a nessuna corsa",
            vehicle_not_moving: "veicolo fermo — la linea sarà rilevata quando si muove",
            no_router_device: "nessun router collegato",
            detection_error: "rilevamento non riuscito",
        },
    },
}

// 'monday'.. -> short localized labels, collapsed to a range when contiguous
// (Mon–Sat / Lun–Sab).
const DAY_SHORT = {
    en: { monday: "Mon", tuesday: "Tue", wednesday: "Wed", thursday: "Thu", friday: "Fri", saturday: "Sat", sunday: "Sun" },
    it: { monday: "Lun", tuesday: "Mar", wednesday: "Mer", thursday: "Gio", friday: "Ven", saturday: "Sab", sunday: "Dom" },
}
const DAY_ORDER = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]

export const formatOperatingDays = (days = [], language = "en") => {
    const labels = DAY_SHORT[language] || DAY_SHORT.en
    const idx = DAY_ORDER.map((d, i) => (days.includes(d) ? i : null)).filter((i) => i !== null)
    if (!idx.length) return ""
    const contiguous = idx.every((v, i) => i === 0 || v === idx[i - 1] + 1)
    if (contiguous && idx.length > 2) {
        return `${labels[DAY_ORDER[idx[0]]]}–${labels[DAY_ORDER[idx[idx.length - 1]]]}`
    }
    return idx.map((i) => labels[DAY_ORDER[i]]).join(", ")
}

// GTFS dates are YYYYMMDD strings -> DD/MM/YYYY for display.
// "Select all" row pinned above a multi-select's options. Mousedown is
// swallowed so the dropdown does not close (antd closes on outside mousedown).
export const SelectAllRow = ({ label, total, selected, onToggle }) => (
    <div className="select-all-row" onMouseDown={(e) => e.preventDefault()}>
        <Checkbox
            checked={total > 0 && selected === total}
            indeterminate={selected > 0 && selected < total}
            onChange={(e) => onToggle(e.target.checked)}
        >
            {label} ({total})
        </Checkbox>
    </div>
)

const formatGtfsDate = (d) =>
    d && String(d).length === 8 ? `${String(d).slice(6, 8)}/${String(d).slice(4, 6)}/${String(d).slice(0, 4)}` : d || "-"

/**
 * GTFS upload for organization admins and superAdmin.
 * - org admin: uploads for their own organization
 * - superAdmin: must pick the organization first
 * After a successful import, the org's vehicles can be attached to the feed
 * so their route matching uses this GTFS.
 */
const GtfsUpload = ({ isVisible, onClose, language, user, organizations = [], targetFeed = null }) => {
    const t = LABELS[language] || LABELS.en
    const isSuperAdmin = user?.role === "superAdmin"
    // Replace mode: targetFeed is the feed being replaced — the organization is
    // fixed and the attach step is skipped (vehicles stay linked to the feed).
    const isReplace = !!targetFeed

    const [selectedOrg, setSelectedOrg] = useState(null)
    const [feedName, setFeedName] = useState("")
    const [gtfsFile, setGtfsFile] = useState(null)
    // Optional companions of an Excel-timetable upload (ignored for GTFS zips).
    // Their pickers are hidden for now; flip this to show them again.
    const SHOW_EXCEL_COMPANIONS = false
    const [turniFiles, setTurniFiles] = useState([])
    const [stopsFiles, setStopsFiles] = useState([])
    const [uploading, setUploading] = useState(false)
    const [summary, setSummary] = useState(null) // set after a successful upload
    const [vehicles, setVehicles] = useState([])
    const [selectedVehicles, setSelectedVehicles] = useState([])
    const [attaching, setAttaching] = useState(false)
    // Per-vehicle line detection results returned by the attach call.
    const [detections, setDetections] = useState(null)
    // Package preview (parsed server-side, nothing imported yet).
    const [preview, setPreview] = useState(null)
    const [previewLoading, setPreviewLoading] = useState(false)
    // Id of the latest preview request: a response that lands after the modal
    // was closed (or after another file was picked) must not repopulate the
    // form with the previous package.
    const previewRequest = useRef(0)

    const organizationId = isReplace ? targetFeed?.organization_id : isSuperAdmin ? selectedOrg : user?.organization_id

    const reset = () => {
        previewRequest.current += 1
        setSelectedOrg(null)
        setFeedName("")
        setGtfsFile(null)
        setTurniFiles([])
        setStopsFiles([])
        setSummary(null)
        setVehicles([])
        setSelectedVehicles([])
        setDetections(null)
        setPreview(null)
        setPreviewLoading(false)
    }

    useEffect(() => {
        if (!isVisible) reset()
        else if (isReplace) setSelectedOrg(targetFeed?.organization_id)
    }, [isVisible])

    const fetchVehicles = async (orgId) => {
        try {
            const params = {
                user_id: user?.id,
                resourceType: "vehicles",
                page: 1,
                limit: 1000,
                searchQuery: "",
                ...(isSuperAdmin ? { organization_id: orgId } : {}),
            }
            const res = await secureApi?.get(GET?.GET_ORGANIZATION_FILTER, { params })
            if (res?.success) setVehicles(res?.data || [])
        } catch (e) {
            setVehicles([])
        }
    }

    // The package plus its optional companions, as the server expects them.
    const packageFormData = (file, turni = turniFiles, stops = stopsFiles) => {
        const formData = new FormData()
        formData.append("file", file)
        turni.forEach((f) => formData.append("turni", f))
        stops.forEach((f) => formData.append("stops", f))
        return formData
    }

    // Parse the selected zip server-side (nothing is imported) so the user can
    // check lines/stops/trips before confirming the upload. Re-run when a
    // roster or stop file is added, since those change the converted result.
    const fetchPreview = async (file, turni = turniFiles, stops = stopsFiles) => {
        const requestId = ++previewRequest.current
        const isCurrent = () => requestId === previewRequest.current
        try {
            setPreviewLoading(true)
            setPreview(null)
            const formData = packageFormData(file, turni, stops)
            if (organizationId && (isSuperAdmin || isReplace)) formData.append("organization_id", organizationId)
            const res = await secureApi.post(POST?.PREVIEW_GTFS, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            })
            if (!isCurrent()) return
            if (res?.success) setPreview(res?.data)
            else message.error([res?.message, res?.error].filter(Boolean).join(": ") || t.previewFailed)
        } catch (e) {
            if (isCurrent()) message.error(e?.response?.data?.message || t.previewFailed)
        } finally {
            if (isCurrent()) setPreviewLoading(false)
        }
    }

    // Only vehicles with no GTFS yet can be attached here; the others are
    // listed locked — a vehicle stays with its feed until detached there.
    const attachableVehicles = vehicles.filter((v) => !v?.gtfs_feed_source)

    const handleUpload = async () => {
        if (!organizationId) {
            return message.error(t.selectOrganization)
        }
        if (!gtfsFile) {
            return message.error(t.selectFile)
        }

        try {
            setUploading(true)
            const formData = packageFormData(gtfsFile)
            if (isSuperAdmin || isReplace) formData.append("organization_id", organizationId)
            if (!isReplace && feedName?.trim()) formData.append("feed_name", feedName.trim())

            const res = await secureApi.post(POST?.UPLOAD_GTFS, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            })

            if (res?.success) {
                message.success(t.uploadSuccess)
                setSummary(res?.data)
                // Replace keeps the existing attachments — no need to re-select vehicles.
                if (!isReplace) fetchVehicles(organizationId)
            } else {
                // Surface the backend's error detail — "GTFS import failed"
                // alone is undiagnosable.
                message.error([res?.message, res?.error].filter(Boolean).join(': ') || "Upload failed")
            }
        } catch (e) {
            message.error(e?.response?.data?.message || "Upload failed")
        } finally {
            setUploading(false)
        }
    }

    const handleAttach = async () => {
        if (!selectedVehicles.length) {
            return message.error(t.selectVehicles)
        }

        try {
            setAttaching(true)
            const res = await secureApi.post(POST?.ATTACH_GTFS_VEHICLES, {
                feed_source: summary?.feed_source,
                vehicle_ids: selectedVehicles,
                ...(isSuperAdmin ? { organization_id: organizationId } : {}),
            })

            if (res?.success) {
                message.success(t.attachSuccess)
                // Show what line each vehicle was detected on instead of
                // closing blind — the operator sees the assignment worked.
                const det = res?.data?.detections
                if (Array.isArray(det) && det.length) setDetections(det)
                else onClose()
            } else {
                message.error(res?.message || "Attach failed")
            }
        } catch (e) {
            message.error(e?.response?.data?.message || "Attach failed")
        } finally {
            setAttaching(false)
        }
    }

    return (
        <CustomModal
            isVisible={isVisible}
            onClose={onClose}
            title={isReplace ? t.replaceTitle : t.title}
            actionText={detections ? t.done : summary ? (isReplace ? t.done : t.attach) : t.upload}
            onAction={detections ? onClose : summary ? (isReplace ? onClose : handleAttach) : handleUpload}
            loading={uploading || attaching}
            language={language}
        >
            <Form layout="vertical">
                {isReplace && !summary && (
                    <div style={{ marginBottom: 16, padding: 12, background: "#e6f4ff", border: "1px solid #91caff", borderRadius: 8, fontSize: 13 }}>
                        {t.replaceHint} {targetFeed?.feed_name ? <strong>({targetFeed.feed_name})</strong> : null}
                    </div>
                )}

                {isSuperAdmin && !isReplace && !summary && (
                    <Form.Item label={t.organization} required>
                        <CustomSelect
                            placeholder={t.selectOrganization}
                            value={selectedOrg}
                            onChange={(val) => setSelectedOrg(val)}
                            filterOption={(input, option) =>
                                String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            options={organizations?.map((org) => ({
                                label: org.organization_name,
                                value: org.id,
                            }))}
                        />
                    </Form.Item>
                )}

                {!summary && !isReplace && (
                    <Form.Item label={t.gtfsName}>
                        <Input
                            className="inp-height-43"
                            placeholder={t.enterGtfsName}
                            value={feedName}
                            onChange={(e) => setFeedName(e.target.value)}
                            maxLength={100}
                        />
                    </Form.Item>
                )}

                {!summary && (
                    <Form.Item label={t.gtfsFile} required extra={t.fileHint}>
                        <Upload
                            accept=".zip"
                            maxCount={1}
                            fileList={gtfsFile ? [gtfsFile] : []}
                            beforeUpload={(file) => {
                                if (!file.name.toLowerCase().endsWith(".zip")) {
                                    message.error(t.selectFile)
                                    return Upload.LIST_IGNORE
                                }
                                setGtfsFile(file)
                                fetchPreview(file)
                                return false
                            }}
                            onRemove={() => {
                                setGtfsFile(null)
                                setPreview(null)
                            }}
                        >
                            <div className="add-button" style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8, padding: "8px 16px" }}>
                                <UploadOutlined /> {t.selectFile}
                            </div>
                        </Upload>
                    </Form.Item>
                )}

                {SHOW_EXCEL_COMPANIONS && !summary && (
                    <Form.Item label={t.turniFiles} extra={t.turniHint}>
                        <Upload
                            accept=".xls,.xlsx"
                            multiple
                            fileList={turniFiles}
                            beforeUpload={(file) => {
                                const next = [...turniFiles, file]
                                setTurniFiles(next)
                                if (gtfsFile) fetchPreview(gtfsFile, next, stopsFiles)
                                return false
                            }}
                            onRemove={(file) => {
                                const next = turniFiles.filter((f) => f.uid !== file.uid)
                                setTurniFiles(next)
                                if (gtfsFile) fetchPreview(gtfsFile, next, stopsFiles)
                            }}
                        >
                            <div className="add-button" style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8, padding: "8px 16px" }}>
                                <UploadOutlined /> {t.selectTurni}
                            </div>
                        </Upload>
                    </Form.Item>
                )}

                {SHOW_EXCEL_COMPANIONS && !summary && (
                    <Form.Item label={t.stopsFiles} extra={t.stopsHint}>
                        <Upload
                            accept=".csv,.txt,.zip"
                            multiple
                            fileList={stopsFiles}
                            beforeUpload={(file) => {
                                const next = [...stopsFiles, file]
                                setStopsFiles(next)
                                if (gtfsFile) fetchPreview(gtfsFile, turniFiles, next)
                                return false
                            }}
                            onRemove={(file) => {
                                const next = stopsFiles.filter((f) => f.uid !== file.uid)
                                setStopsFiles(next)
                                if (gtfsFile) fetchPreview(gtfsFile, turniFiles, next)
                            }}
                        >
                            <div className="add-button" style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8, padding: "8px 16px" }}>
                                <UploadOutlined /> {t.selectStops}
                            </div>
                        </Upload>
                    </Form.Item>
                )}

                {!summary && (previewLoading || preview) && (
                    <div style={{ marginBottom: 16, padding: 12, background: "#fafafa", border: "1px solid #e5e5e5", borderRadius: 8 }}>
                        {previewLoading ? (
                            <div style={{ fontSize: 13, color: "#595959" }}>{t.loadingPreview}</div>
                        ) : preview ? (
                            <>
                                <div style={{ fontWeight: 600, marginBottom: 8 }}>{t.previewTitle}</div>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8, fontSize: 12 }}>
                                    {[
                                        [t.linesLbl, preview.lines?.length],
                                        [t.stopsLbl, preview.stops],
                                        [t.tripsLbl, preview.trips],
                                        [t.shiftsLbl, preview.shifts],
                                        [t.shapesLbl, preview.shapes],
                                    ].map(([label, val]) => (
                                        <span key={label} style={{ background: "#fff", border: "1px solid #e5e5e5", borderRadius: 12, padding: "2px 10px" }}>
                                            {label}: <strong>{val ?? 0}</strong>
                                        </span>
                                    ))}
                                </div>
                                <div style={{ fontSize: 12, color: "#595959", marginBottom: 8 }}>
                                    {t.servicePeriod}: <strong>{formatGtfsDate(preview.startDate)} – {formatGtfsDate(preview.endDate)}</strong>
                                    {preview.days?.length ? <> · {t.runsOn} {formatOperatingDays(preview.days, language)}</> : null}
                                </div>
                                {preview.conversion && (
                                    <div style={{ marginBottom: 8, padding: 10, background: "#fff", border: "1px solid #e5e5e5", borderRadius: 6, fontSize: 12 }}>
                                        <div style={{ fontWeight: 600, marginBottom: 6 }}>{t.convertedTitle}</div>
                                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 6 }}>
                                            {[
                                                [t.workbooksLbl, preview.conversion.workbooks?.length],
                                                [t.blockIdsLbl, `${preview.conversion.blockIds?.matched ?? 0} / ${preview.conversion.trips ?? 0}`],
                                                [t.coordsResolved, preview.conversion.stopCoordinates?.resolved],
                                                [t.coordsUnresolved, preview.conversion.stopCoordinates?.unresolved],
                                            ].map(([label, val]) => (
                                                <span key={label} style={{ background: "#fafafa", border: "1px solid #e5e5e5", borderRadius: 12, padding: "2px 10px" }}>
                                                    {label}: <strong>{val ?? 0}</strong>
                                                </span>
                                            ))}
                                        </div>
                                        {preview.conversion.stopCoordinates?.unresolved > 0 && (
                                            <div style={{ padding: 8, background: "#fff8e6", border: "1px solid #f2e3b3", borderRadius: 6, marginBottom: 6 }}>
                                                ⚠️ {t.coordsWarning}
                                            </div>
                                        )}
                                        {preview.conversion.issues?.tripsDropped?.length > 0 && (
                                            <div style={{ color: "#8c8c8c" }}>
                                                {t.tripsDroppedLbl}: {preview.conversion.issues.tripsDropped.join(", ")}
                                            </div>
                                        )}
                                        {preview.conversion.stopCoordinates?.unresolvedStops?.length > 0 && (
                                            <details style={{ marginTop: 4 }}>
                                                <summary style={{ cursor: "pointer" }}>{t.unresolvedList} ({preview.conversion.stopCoordinates.unresolvedStops.length})</summary>
                                                <div style={{ maxHeight: 120, overflowY: "auto", marginTop: 4 }}>
                                                    {preview.conversion.stopCoordinates.unresolvedStops.map((s) => (
                                                        <div key={s.stop_id}>{s.stop_name}{s.stop_code ? ` (${s.stop_code})` : ""}</div>
                                                    ))}
                                                </div>
                                            </details>
                                        )}
                                    </div>
                                )}
                                <div style={{ maxHeight: 180, overflowY: "auto", background: "#fff", border: "1px solid #f0f0f0", borderRadius: 6 }}>
                                    {preview.lines?.map((l) => (
                                        <div key={l.route_id} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "6px 10px", borderBottom: "1px solid #f5f5f5", fontSize: 13 }}>
                                            <span>
                                                <strong>{l.route_short_name || "-"}</strong>
                                                {l.route_long_name ? <span style={{ color: "#8c8c8c" }}> — {l.route_long_name}</span> : null}
                                            </span>
                                            <span style={{ color: "#8c8c8c", whiteSpace: "nowrap" }}>{l.trips} {t.tripsLbl.toLowerCase()}</span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        ) : null}
                    </div>
                )}

                {detections && (
                    <div style={{ marginBottom: 8 }}>
                        {detections.map((d) => {
                            const v = vehicles.find((x) => x.id === d.vehicle_id)
                            const name = v?.vehicle_name || d.vehicle_id
                            return (
                                <div
                                    key={d.vehicle_id}
                                    style={{
                                        padding: "10px 14px",
                                        marginBottom: 8,
                                        borderRadius: 8,
                                        border: `1px solid ${d.matched ? "#b7eb8f" : "#f2e3b3"}`,
                                        background: d.matched ? "#f6ffed" : "#fff8e6",
                                        fontSize: 13,
                                    }}
                                >
                                    <strong>{name}</strong>{" — "}
                                    {d.matched ? (
                                        <>
                                            {t.detected}: <strong>{d.route_short_name}</strong> {d.route_long_name}
                                            {d.days?.length ? <> · {t.runsOn} {formatOperatingDays(d.days, language)}</> : null}
                                            {" · "}{d.stop_count} stops
                                        </>
                                    ) : d.default_assigned ? (
                                        <>
                                            {t.defaultAssigned}: <strong>{d.route_short_name}</strong> {d.route_long_name}
                                            {d.days?.length ? <> · {t.runsOn} {formatOperatingDays(d.days, language)}</> : null}
                                            {" · "}{t.willVerify}
                                        </>
                                    ) : (
                                        <>{t.notDetected} ({t.reasons[d.reason] || d.reason})</>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                )}

                {summary && !detections && isReplace && (
                    <div style={{ marginBottom: 16, padding: 12, background: "#f6ffed", border: "1px solid #b7eb8f", borderRadius: 8 }}>
                        ✅ {t.uploadSuccess} — {t.summary(summary)}
                        <div style={{ marginTop: 6, fontSize: 13, color: "#595959" }}>{t.replaceNote}</div>
                    </div>
                )}
                {summary && !detections && summary?.conversion?.stopCoordinates?.background && (
                    <div style={{ marginBottom: 16, padding: 12, background: "#fff8e6", border: "1px solid #f2e3b3", borderRadius: 8, fontSize: 13 }}>
                        📍 {t.coordsBackground(summary.conversion.stopCoordinates.unresolved)}
                    </div>
                )}

                {summary && !detections && !isReplace && (
                    <>
                        <div style={{ marginBottom: 16, padding: 12, background: "#f6ffed", border: "1px solid #b7eb8f", borderRadius: 8 }}>
                            ✅ {t.uploadSuccess} — {t.summary(summary)}
                        </div>

                        {isSuperAdmin && (
                            // The feed was imported for the organization picked
                            // above; the list below is that organization's fleet.
                            <Form.Item label={t.organization} extra={t.attachOrgHint}>
                                <Select
                                    disabled
                                    style={{ width: "100%" }}
                                    value={organizationId}
                                    options={organizations?.map((org) => ({
                                        label: org.organization_name,
                                        value: org.id,
                                    }))}
                                />
                            </Form.Item>
                        )}

                        <Form.Item
                            label={t.attachVehicles}
                            extra={
                                <>
                                    {attachableVehicles.length
                                        ? t.selectedCount(selectedVehicles.length, attachableVehicles.length)
                                        : null}
                                    {vehicles.length > attachableVehicles.length && (
                                        <div>{t.attachLockedHint}</div>
                                    )}
                                </>
                            }
                        >
                            <Select
                                mode="multiple"
                                allowClear
                                showSearch
                                maxTagCount="responsive"
                                placeholder={t.selectVehicles}
                                value={selectedVehicles}
                                onChange={setSelectedVehicles}
                                style={{ width: "100%" }}
                                notFoundContent={t.noVehiclesForOrg}
                                filterOption={(input, option) =>
                                    String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                                }
                                options={vehicles.map((v) => ({
                                    label: `${vehicleLabel(v) || v.id}${v?.gtfs_feed_source ? ` — ${t.attachedElsewhere}` : ""}`,
                                    value: v.id,
                                    disabled: !!v?.gtfs_feed_source,
                                }))}
                                dropdownRender={(menu) => (
                                    <>
                                        {attachableVehicles.length > 0 && (
                                            <SelectAllRow
                                                label={t.selectAll}
                                                total={attachableVehicles.length}
                                                selected={selectedVehicles.length}
                                                onToggle={(checked) =>
                                                    setSelectedVehicles(checked ? attachableVehicles.map((v) => v.id) : [])
                                                }
                                            />
                                        )}
                                        {menu}
                                    </>
                                )}
                            />
                        </Form.Item>
                    </>
                )}
            </Form>
        </CustomModal>
    )
}

export default GtfsUpload
